import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Badge } from "react-bootstrap";
import "../componentCss/webform.css";
import "../componentCss/sidebar.css";
import "../componentCss/healthcare-modern.css";
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';
import DataTable from "react-data-table-component";
import { getConsentForms, getStudentsConsentStatus, exportStudentsConsent } from "../../services/consentService";
import { BsCheckCircleFill, BsXCircleFill, BsSearch, BsDownload } from "react-icons/bs";
import { FaClipboardList, FaUsers } from "react-icons/fa";

const ConsentManagement = () => {
  // State for consent forms dropdown
  const [consentForms, setConsentForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState('');

  // State for consent status toggle
  const [consentDone, setConsentDone] = useState(true);

  // State for students list
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    done: 0,
    pending: 0
  });

  // Load consent forms on mount
  useEffect(() => {
    loadConsentForms();
  }, []);

  // Load students when form is selected or filters change
  useEffect(() => {
    if (selectedFormId) {
      loadStudents();
    }
  }, [selectedFormId, consentDone, currentPage, rowsPerPage]);

  // Debounced search
  useEffect(() => {
    if (selectedFormId) {
      const timer = setTimeout(() => {
        setCurrentPage(1);
        loadStudents();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm]);

  const loadConsentForms = async () => {
    const response = await getConsentForms();
    if (response.success && response.data?.forms) {
      setConsentForms(response.data.forms);
      // Auto-select first form if available
      if (response.data.forms.length > 0) {
        setSelectedFormId(response.data.forms[0].form_id);
      }
    } else {
      toast.error(response.message || 'Failed to load consent forms');
    }
  };

  const loadStudents = async (isPagination = false) => {
    if (!selectedFormId) return;

    if (isPagination) {
      setPaginationLoading(true);
    } else {
      setIsLoading(true);
    }

    try {
      const params = {
        form_id: selectedFormId,
        consent_done: consentDone,
        page: currentPage,
        page_size: rowsPerPage,
        search: searchTerm || undefined
      };

      const response = await getStudentsConsentStatus(params);

      if (response.success && response.data) {
        setStudents(response.data.students || []);
        setTotalRows(response.data.meta?.total_items || 0);

        // Update stats based on current view
        if (consentDone) {
          setStats(prev => ({ ...prev, done: response.data.meta?.total_items || 0 }));
        } else {
          setStats(prev => ({ ...prev, pending: response.data.meta?.total_items || 0 }));
        }
      } else {
        setStudents([]);
        setTotalRows(0);
        if (response.message && !response.message.includes('Failed to fetch')) {
          toast.error(response.message);
        }
      }
    } catch (error) {
      console.error('Error loading students:', error);
      setStudents([]);
      setTotalRows(0);
    } finally {
      if (isPagination) {
        setPaginationLoading(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleFormChange = (e) => {
    setSelectedFormId(e.target.value);
    setCurrentPage(1);
  };

  const handleToggleChange = (isDone) => {
    setConsentDone(isDone);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadStudents(true);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleExport = async (format) => {
    if (!selectedFormId) {
      toast.error('Please select a consent form first');
      return;
    }
    setIsExporting(true);
    try {
      const params = {
        form_id: selectedFormId,
        consent_done: consentDone,
        format: format,
        search: searchTerm || undefined
      };
      const response = await exportStudentsConsent(params);
      const disposition = response.headers['content-disposition'];
      let filename = `consent_export.${format}`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      saveAs(new Blob([response.data]), filename);
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const columns = [
    {
      name: "#",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      width: "60px",
      sortable: false
    },
    {
      name: "Student Name",
      selector: (row) => row.student_name || 'Unknown',
      sortable: true,
      width: "25%"
    },
    {
      name: "Class",
      selector: (row) => row.student_class || '-',
      sortable: true,
      width: "12%"
    },
    {
      name: "Section",
      selector: (row) => row.student_section || '-',
      sortable: true,
      width: "12%"
    },
    {
      name: "Status",
      cell: (row) => (
        <Badge
          bg={row.consent_done ? "success" : "warning"}
          className="d-flex align-items-center gap-1"
        >
          {row.consent_done ? (
            <>
              <BsCheckCircleFill size={12} />
              Done
            </>
          ) : (
            <>
              <BsXCircleFill size={12} />
              Pending
            </>
          )}
        </Badge>
      ),
      width: "12%"
    },
    {
      name: "Submission Date",
      selector: (row) => formatDate(row.submission_date),
      sortable: true,
      width: "15%"
    },
    {
      name: "Submitted By",
      selector: (row) => row.submitted_by_name || row.submitted_by || '-',
      sortable: true,
      width: "20%"
    }
  ];

  return (
    <div id="content" className="healthcare-container">
      <Container fluid className="p-4">
        {/* Header */}
        <Row>
          <Col lg={12}>
            <div className="healthcare-card">
              <div className="healthcare-header">
                <h5>
                  <FaClipboardList size={24} />
                  Consent Management
                </h5>
                <div className="subtitle">
                  View and track student consent form submissions
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filters */}
        <Row className="mt-4">
          <Col lg={12}>
            <div className="healthcare-card">
              <div className="p-4">
                <Row className="align-items-end g-3">
                  {/* Consent Form Dropdown */}
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Consent Form</Form.Label>
                      <Form.Select
                        value={selectedFormId}
                        onChange={handleFormChange}
                        className="healthcare-select"
                      >
                        <option value="">Select a consent form...</option>
                        {consentForms.map((form) => (
                          <option key={form.form_id} value={form.form_id}>
                            {form.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Toggle for Done/Not Done */}
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Consent Status</Form.Label>
                      <div className="d-flex gap-2">
                        <button
                          className={`healthcare-btn ${consentDone ? 'healthcare-btn-primary' : 'healthcare-btn-secondary'}`}
                          onClick={() => handleToggleChange(true)}
                        >
                          <BsCheckCircleFill size={14} />
                          Done
                        </button>
                        <button
                          className={`healthcare-btn ${!consentDone ? 'healthcare-btn-primary' : 'healthcare-btn-secondary'}`}
                          onClick={() => handleToggleChange(false)}
                        >
                          <BsXCircleFill size={14} />
                          Not Done
                        </button>
                      </div>
                    </Form.Group>
                  </Col>

                  {/* Search */}
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Search Student</Form.Label>
                      <div className="healthcare-search-container">
                        <BsSearch className="search-icon" />
                        <input
                          type="text"
                          className="healthcare-search-input"
                          placeholder="Search by student name..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>

        {/* Students Table */}
        <Row className="mt-4">
          <Col lg={12}>
            <div className="healthcare-card">
              <div className="p-4">
                <div className="healthcare-table-header mb-3">
                  <div>
                    <div className="healthcare-table-title">
                      <FaUsers />
                      Students List
                    </div>
                    <div className="healthcare-table-subtitle">
                      {consentDone ? 'Students who have completed the consent form' : 'Students with pending consent'}
                    </div>
                  </div>
                  <div className="healthcare-table-actions">
                    <button
                      className="healthcare-btn healthcare-btn-success"
                      onClick={() => handleExport('xlsx')}
                      disabled={isExporting || !selectedFormId}
                    >
                      <BsDownload /> Export XLSX
                    </button>
                    <button
                      className="healthcare-btn healthcare-btn-primary"
                      onClick={() => handleExport('pdf')}
                      disabled={isExporting || !selectedFormId}
                    >
                      <BsDownload /> Export PDF
                    </button>
                  </div>
                </div>

                {!selectedFormId ? (
                  <div className="text-center py-5 text-muted">
                    <FaClipboardList size={48} className="mb-3" />
                    <p>Please select a consent form to view student list</p>
                  </div>
                ) : isLoading && !paginationLoading ? (
                  <div className="healthcare-loading">
                    <div className="healthcare-loading-spinner"></div>
                    <div className="healthcare-loading-text">Loading students...</div>
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={students}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                    paginationComponentOptions={{
                      rowsPerPageText: 'Rows per page:',
                      rangeSeparatorText: 'of',
                    }}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    progressPending={paginationLoading}
                    progressComponent={
                      <div className="d-flex justify-content-center align-items-center p-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="ms-2">Loading students...</span>
                      </div>
                    }
                    noDataComponent={
                      <div className="text-center py-5 text-muted">
                        <FaUsers size={48} className="mb-3" />
                        <p>
                          {consentDone
                            ? 'No students have completed this consent form yet'
                            : 'All students have completed this consent form'}
                        </p>
                      </div>
                    }
                    highlightOnHover
                    striped
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ConsentManagement;
